/**
 * Provider CRUD — all operations scoped to organizations/{orgId}/providers.
 */

import { addDoc, deleteDoc, doc, getDocs, query, where, writeBatch, type UpdateData } from 'firebase/firestore'
import { db } from '../config'
import type { Provider } from '../types'
import { providersCollection, providerRef } from './firestore'
import { logStaffEdit } from './audit'

/** Create a new provider. Returns the generated Firestore ID. */
export async function addProvider(orgId: string, provider: Omit<Provider, 'id'>): Promise<string> {
  const ref = await addDoc(providersCollection(orgId), { ...provider, id: '' } as Provider)
  logStaffEdit('providers', ref.id, 'create')
  return ref.id
}

/**
 * Atomically save a provider together with its sibling default-clears (#98).
 * The one-default-per-clinic invariant spans documents, so committing the
 * claim and the clears in one WriteBatch prevents the partial states a
 * sequential Promise.all could leave behind: siblings cleared but the main
 * save failed (clinic ends up with NO default), or some siblings cleared
 * and others not. Pass `providerId` to update an existing doc; omit it to
 * create. Returns the saved provider's id.
 */
export async function saveProviderWithDefaults(
  orgId: string,
  fields: Partial<Omit<Provider, 'id'>>,
  siblingUpdates: Array<{ id: string; fields: Partial<Omit<Provider, 'id'>> }>,
  providerId?: string,
): Promise<string> {
  const batch = writeBatch(db)
  for (const s of siblingUpdates) {
    batch.update(providerRef(orgId, s.id), s.fields as UpdateData<Provider>)
  }
  let id = providerId
  if (id) {
    batch.update(providerRef(orgId, id), fields as UpdateData<Provider>)
  } else {
    const ref = doc(providersCollection(orgId))
    id = ref.id
    batch.set(ref, { ...fields, id: '' } as Provider)
  }
  await batch.commit()
  logStaffEdit('providers', id, providerId ? 'update' : 'create')
  for (const s of siblingUpdates) {
    logStaffEdit('providers', s.id, 'update')
  }
  return id
}

/**
 * List providers for an organization, optionally filtered by clinic
 * membership.
 *
 * The clinic-scoped path is a server-side `array-contains` query — it has
 * to be, because plain users' read access is granted per-doc by that same
 * membership (firestore.rules), so an unfiltered fetch would be denied.
 * Known gap: pre-#98 legacy docs (single `clinicId`, no array) don't match
 * until scripts/migrate-provider-clinics.mjs runs — the deploy runbook
 * runs expand BEFORE the deploy (deploy-first would leave front-desk
 * dropdowns empty on legacy docs), then re-runs it after to catch docs
 * the old code created or edited in between.
 */
export async function listProviders(orgId: string, clinicId?: string): Promise<Provider[]> {
  const ref = providersCollection(orgId)
  const q = clinicId ? query(ref, where('clinicIds', 'array-contains', clinicId)) : ref
  const snap = await getDocs(q)
  return snap.docs.map(d => d.data())
}

/** Delete a provider by ID. */
export async function deleteProvider(orgId: string, providerId: string): Promise<void> {
  await deleteDoc(providerRef(orgId, providerId))
  logStaffEdit('providers', providerId, 'delete')
}
